<template>
  <div>
    <head>
      <title>Scheduled Procedures | County Hospital</title>
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <link rel="shortcut icon" href="assets/images/favicon.svg">
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body @contextmenu.prevent data-sidebar="dark">
    <div id="layout-wrapper">
      <NavigationBar/>
    <div id="content" class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">

          <!-- Header -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Scheduled Procedures</h4>
                <button class="btn btn-sm btn-primary" @click="$router.push('schedule-procedure')">
                  <i class="bx bx-plus me-1"></i> Schedule New
                </button>
              </div>
            </div>
          </div>

          <!-- Filters -->
          <div class="row g-2 mb-3 align-items-end">
            <div class="col-md-2">
              <label class="form-label small mb-1">Status</label>
              <select class="form-select form-select-sm" v-model="filters.status" @change="load">
                <option value="">All Active</option>
                <option value="SCHEDULED">Scheduled</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="POSTPONED">Postponed</option>
                <option value="COMPLETED">Completed</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            </div>
            <div class="col-md-2">
              <label class="form-label small mb-1">Priority</label>
              <select class="form-select form-select-sm" v-model="filters.priority" @change="load">
                <option value="">All</option>
                <option value="ROUTINE">Routine</option>
                <option value="URGENT">Urgent</option>
                <option value="EMERGENCY">Emergency</option>
              </select>
            </div>
            <div class="col-md-2">
              <label class="form-label small mb-1">From</label>
              <input type="date" class="form-control form-control-sm" v-model="filters.dateFrom">
            </div>
            <div class="col-md-2">
              <label class="form-label small mb-1">To</label>
              <input type="date" class="form-control form-control-sm" v-model="filters.dateTo">
            </div>
            <div class="col-md-3">
              <label class="form-label small mb-1">Search</label>
              <input type="text" class="form-control form-control-sm"
                     v-model.trim="search"
                     placeholder="patient, procedure, doctor, ref...">
            </div>
            <div class="col-md-1">
              <button class="btn btn-primary btn-sm w-100" @click="load">
                <i class="bx bx-filter"></i>
              </button>
            </div>
          </div>

          <!-- KPI strip -->
          <div class="row g-2 mb-3">
            <div class="col-3"><div class="card border text-center p-2 mb-0"><small class="text-muted">Total</small><h5 class="mb-0">{{ filtered.length }}</h5></div></div>
            <div class="col-3"><div class="card border text-center p-2 mb-0"><small class="text-muted">Today</small><h5 class="mb-0 text-primary">{{ counts.today }}</h5></div></div>
            <div class="col-3"><div class="card border text-center p-2 mb-0"><small class="text-warning">Urgent</small><h5 class="mb-0 text-warning">{{ counts.urgent }}</h5></div></div>
            <div class="col-3"><div class="card border text-center p-2 mb-0"><small class="text-danger">Emergency</small><h5 class="mb-0 text-danger">{{ counts.emergency }}</h5></div></div>
          </div>

          <!-- States -->
          <div v-if="loading" class="text-center text-muted py-4">
            <span class="spinner-border spinner-border-sm me-1"></span>Loading…
          </div>
          <div v-else-if="!filtered.length" class="text-center text-muted py-4">
            No procedures match these filters.
          </div>

          <!-- Cards on phone, table on tablet+ -->
          <div v-else>
            <!-- Phone cards -->
            <div class="d-md-none">
              <div v-for="p in filtered" :key="'card-'+p.procedureID" class="card border mb-2 shadow-sm" @click="openDetail(p)" style="cursor:pointer;">
                <div class="card-body p-3">
                  <div class="d-flex justify-content-between align-items-start mb-1">
                    <strong>{{ p.procedureName }}</strong>
                    <span class="badge" :class="statusBadge(p.procedureStatus)">{{ p.procedureStatus }}</span>
                  </div>
                  <div>{{ p.patientName }} <small class="text-muted">#{{ p.patientNo }}</small></div>
                  <div class="text-muted small">
                    {{ formatDate(p.scheduledDate) }} {{ formatTime(p.scheduledTime) }} · Dr. {{ p.performingDoctorName || '—' }}
                  </div>
                  <span class="badge mt-1" :class="priorityBadge(p.priority)">{{ p.priority }}</span>
                </div>
              </div>
            </div>

            <!-- Tablet/desktop table -->
            <div class="table-responsive d-none d-md-block">
              <table class="table table-sm table-hover align-middle">
                <thead class="table-light">
                <tr>
                  <th>Reference</th>
                  <th>Patient</th>
                  <th>Procedure</th>
                  <th>Date / Time</th>
                  <th>Doctor</th>
                  <th>Priority</th>
                  <th>Status</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="p in filtered" :key="'row-'+p.procedureID" @click="openDetail(p)" style="cursor:pointer;">
                  <td><code>{{ p.procedureNo }}</code></td>
                  <td><strong>{{ p.patientName }}</strong><br><small class="text-muted">#{{ p.patientNo }}</small></td>
                  <td>{{ p.procedureName }}</td>
                  <td>{{ formatDate(p.scheduledDate) }}<br><small class="text-muted">{{ formatTime(p.scheduledTime) }}</small></td>
                  <td>{{ p.performingDoctorName || '—' }}</td>
                  <td><span class="badge" :class="priorityBadge(p.priority)">{{ p.priority }}</span></td>
                  <td><span class="badge" :class="statusBadge(p.procedureStatus)">{{ p.procedureStatus }}</span></td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Detail modal -->
          <div class="modal fade" id="procedureDetail" tabindex="-1">
            <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div class="modal-content" v-if="selected">
                <div class="modal-header">
                  <div>
                    <h5 class="modal-title">{{ selected.procedureName }}</h5>
                    <small class="text-muted">{{ selected.procedureNo }}</small>
                  </div>
                  <button class="btn-close" data-bs-dismiss="modal"></button>
                </div>
                <div class="modal-body">
                  <div class="row g-3 mb-3">
                    <div class="col-md-6"><div class="text-muted small">Patient</div><strong>{{ selected.patientName }}</strong> <small>#{{ selected.patientNo }}</small></div>
                    <div class="col-md-6"><div class="text-muted small">Visit</div>{{ selected.visitNo || selected.visitID || '—' }}</div>
                    <div class="col-md-6"><div class="text-muted small">Scheduled</div>{{ formatDate(selected.scheduledDate) }} {{ formatTime(selected.scheduledTime) }}</div>
                    <div class="col-md-6"><div class="text-muted small">Doctor</div>{{ selected.performingDoctorName || '—' }}</div>
                    <div class="col-md-6"><div class="text-muted small">Priority</div><span class="badge" :class="priorityBadge(selected.priority)">{{ selected.priority }}</span></div>
                    <div class="col-md-6"><div class="text-muted small">Status</div><span class="badge" :class="statusBadge(selected.procedureStatus)">{{ selected.procedureStatus }}</span></div>
                  </div>
                  <h6>Pre-op Diagnosis</h6>
                  <p class="text-muted">{{ selected.preOpDiagnosis || '—' }}</p>
                  <h6 v-if="selected.notes">Notes</h6>
                  <p class="text-muted" v-if="selected.notes">{{ selected.notes }}</p>
                  <template v-if="selected.procedureStatus === 'COMPLETED'">
                    <h6>Post-op Diagnosis</h6>
                    <p class="text-muted">{{ selected.postOpDiagnosis || '—' }}</p>
                    <h6>Outcome</h6>
                    <p class="text-muted">{{ selected.outcome || '—' }}</p>
                    <h6 v-if="selected.complications">Complications</h6>
                    <p class="text-muted" v-if="selected.complications">{{ selected.complications }}</p>
                  </template>
                </div>
                <div class="modal-footer">
                  <button class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                </div>
              </div>
            </div>
          </div>

          </div>
        </div>
      </div>
    </div>
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import { execute, hospital, getRight } from "@/api";

export default {
  name: "ScheduledProcedures",
  components: { NavigationBar },
  data() {
    return {
      loading: false,
      procedures: [],
      filters: { status: '', priority: '', dateFrom: '', dateTo: '' },
      search: '',
      selected: null
    };
  },
  computed: {
    filtered() {
      const q = (this.search || '').toLowerCase().trim();
      if (!q) return this.procedures;
      return this.procedures.filter(p => {
        const blob = [
          p.procedureNo, p.patientNo, p.patientName, p.procedureName,
          p.performingDoctorName, p.preOpDiagnosis
        ].filter(Boolean).join(' ').toLowerCase();
        return blob.includes(q);
      });
    },
    counts() {
      const today = new Date().toISOString().slice(0, 10);
      let t = 0, u = 0, e = 0;
      for (const p of this.filtered) {
        if (p.scheduledDate === today) t++;
        if (p.priority === 'URGENT') u++;
        if (p.priority === 'EMERGENCY') e++;
      }
      return { today: t, urgent: u, emergency: e };
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    getRight(t) { return getRight(t); },
    load() {
      this.loading = true;
      const data = new FormData();
      data.append('function', 'getScheduledProcedures');
      const facilityID = sessionStorage.getItem('facilityID') || '';
      if (facilityID) data.append('facilityID', facilityID);
      if (this.filters.status)   data.append('status',   this.filters.status);
      if (this.filters.priority) data.append('priority', this.filters.priority);
      if (this.filters.dateFrom) data.append('dateFrom', this.filters.dateFrom);
      if (this.filters.dateTo)   data.append('dateTo',   this.filters.dateTo);
      execute(data, hospital).then(res => {
        this.loading = false;
        if (res.data && res.data.success) {
          this.procedures = res.data.data.procedures || [];
        } else {
          this.procedures = [];
        }
      }).catch(() => {
        this.loading = false;
        this.procedures = [];
      });
    },
    openDetail(p) {
      this.selected = p;
      this.$nextTick(() => {
        const el = document.getElementById('procedureDetail');
        if (el && window.bootstrap) new window.bootstrap.Modal(el).show();
      });
    },
    formatDate(d) {
      if (!d) return '—';
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return d;
      return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    },
    formatTime(t) {
      return t ? t.toString().slice(0, 5) : '';
    },
    priorityBadge(p) {
      if (p === 'EMERGENCY') return 'bg-danger';
      if (p === 'URGENT')    return 'bg-warning text-dark';
      return 'bg-secondary';
    },
    statusBadge(s) {
      const m = {
        SCHEDULED: 'bg-info',
        IN_PROGRESS: 'bg-primary',
        COMPLETED: 'bg-success',
        POSTPONED: 'bg-warning text-dark',
        CANCELLED: 'bg-secondary'
      };
      return m[s] || 'bg-secondary';
    }
  }
};
</script>
